import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { getTicketsForTenantApi } from '../../apis/apis'
import { Paginated, TenantTicket, TicketSearchParams } from '../../apis/types'
import type { RootState, AppDispatch } from '../../shared/redux/store'

const SLICE_NAME = 'TICKETS_SLICE'

export const getTickets = createAsyncThunk<
  Paginated<TenantTicket>,
  { tenantCuid: string } & TicketSearchParams,
  {
    state: RootState
    dispatch: AppDispatch
    rejectValue: { err: boolean }
  }
>(`${SLICE_NAME}/getTickets`, async ({ tenantCuid, ...query }, { rejectWithValue }) => {
  try {
    const response = await getTicketsForTenantApi(tenantCuid, query)
    return { ...response }
  } catch (err) {
    return rejectWithValue({
      err: err !== undefined,
    })
  }
})

interface TicketsState {
  loading: boolean
  error: boolean
  ticketRequestPhase: { kind: 'idle' } | { kind: 'in-progress' } | { kind: 'failed' }
  tickets: TenantTicket[]
  total: number
  resultsPerPage: number
  page: number
}

const initialState: TicketsState = {
  loading: false,
  error: false,
  ticketRequestPhase: { kind: 'idle' },
  tickets: [],
  total: 0,
  resultsPerPage: 0,
  page: 0,
}

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    clearTickets: (state) => {
      state.tickets = []
      state.total = 0
      state.page = 0
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getTickets.pending, (state) => {
      state.ticketRequestPhase = { kind: 'in-progress' }
    })
    builder.addCase(getTickets.fulfilled, (state, { payload }) => {
      state.ticketRequestPhase = { kind: 'idle' }
      state.tickets = payload.results || []
      state.total = payload.total ?? 0
      state.resultsPerPage = payload.resultsPerPage ?? 0
      state.page = payload.page ?? 0
    })
    builder.addCase(getTickets.rejected, (state, { payload, error }) => {
      if (payload != null) {
        state.ticketRequestPhase = { kind: 'failed' }
      } else {
        state.error = error !== undefined
      }
    })
  },
})

export const { clearTickets } = slice.actions
export const ticketsReducer = slice.reducer
